// src/components/product/ProductsTable.tsx
//
// ProductsTable
// - Renders list controls via StandardTableToolbar + StandardPagination.
// - Uses StandardTableFrame for loading/empty states.
// - Navigation policy: index-only navigation (consistent with other list pages).
//   - Index(#) click -> /products/:productId
// - Row status is derived from productStatus (warning/inactive/normal).

import React, { useCallback, useMemo } from 'react';
import { Box, Chip, Button, TableCell, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { toUsdDisplay } from '@utils/money';
import { ProductStatus } from '@models/product';
import { ProductSaleType } from '@apis/product';
import { PRODUCT_STATUS_LABELS, PRODUCT_SALE_TYPE_LABELS } from '@utils/productLabels';

import {
  StandardTableToolbar,
  StandardPagination,
  StandardTableFrame,
  TableRowStatus,
  cellSx,
} from '@components/common/table';

// TODO: Replace with real ProductResponse when available.
type ProductRow = any;

interface ProductsTableProps {
  data: ProductRow[];
  isLoading: boolean;
  totalCount?: number;
  page: number;
  pageSize: number;

  /**
   * Page change handler (page number only).
   * Keeping this simple avoids passing synthetic events across layers.
   */
  onChangePage: (nextPage: number) => void;
}

/** Return user-friendly label for product status. */
const getProductStatusLabel = (status?: ProductStatus | string | null) => {
  if (!status) return '-';
  const value = String(status) as ProductStatus | string;
  return PRODUCT_STATUS_LABELS[value as ProductStatus] ?? value;
};

/** Map product status -> Chip color. */
const getProductStatusChipColor = (
  status?: ProductStatus | string | null,
): 'default' | 'success' | 'warning' | 'info' => {
  if (!status) return 'default';

  const value = String(status);
  if (value === ProductStatus.ACTIVE) return 'success';
  if (value === ProductStatus.OUT_OF_STOCK) return 'warning';
  if (value === ProductStatus.DISCONTINUED) return 'default';

  return 'default';
};

/** Return user-friendly label for sale type. */
const getSaleTypeLabel = (saleType?: ProductSaleType | string | null) => {
  if (!saleType) return '-';
  const value = String(saleType) as ProductSaleType | string;
  return PRODUCT_SALE_TYPE_LABELS[value as ProductSaleType] ?? value;
};

/** Decide Chip variant by sale type. */
const getSaleTypeChipVariant = (saleType?: ProductSaleType | string | null): 'filled' | 'outlined' => {
  const value = String(saleType);
  if (value === ProductSaleType.REAL_TIME) return 'filled';
  if (value === ProductSaleType.RESERVATION) return 'outlined';
  return 'outlined';
};

const ProductsTable = ({ data, isLoading, totalCount = 0, page, pageSize, onChangePage }: ProductsTableProps) => {
  const navigate = useNavigate();
  const rows = useMemo(() => data ?? [], [data]);

  const handleOpenDetail = useCallback(
    (productId: number) => {
      if (!Number.isFinite(productId) || productId <= 0) return;
      navigate(`/products/${productId}`);
    },
    [navigate],
  );

  const getRowStatus = useCallback((row: ProductRow): TableRowStatus => {
    const statusValue = String(row.productStatus ?? '');
    if (statusValue === ProductStatus.OUT_OF_STOCK) return 'warning';
    if (statusValue === ProductStatus.DISCONTINUED) return 'inactive';
    return 'normal';
  }, []);

  // (# + Product Code + Product Name + Stock Code + Order Type + Price + Shipped(7d) + Shipped(total) + Refunded + Status)
  const columnsCount = 10;

  return (
    <Box>
      <StandardTableToolbar
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onChangePage={onChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<ProductRow>
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          rows={rows}
          getRowKey={(row) => String(row.productId)}
          getRowStatus={getRowStatus}
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell sx={cellSx.nowrap}>Product Code</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Product Name</TableCell>
              <TableCell width={140} align="center" sx={cellSx.nowrap}>
                Stock Code
              </TableCell>
              <TableCell width={140} align="center" sx={cellSx.nowrap}>
                Order Type
              </TableCell>
              <TableCell width={120} align="right" sx={cellSx.nowrap}>
                Price
              </TableCell>
              <TableCell width={170} align="right" sx={cellSx.nowrap}>
                Shipped (Last 7 days)
              </TableCell>
              <TableCell width={150} align="right" sx={cellSx.nowrap}>
                Shipped (Total)
              </TableCell>
              <TableCell width={120} align="right" sx={cellSx.nowrap}>
                Refunded
              </TableCell>
              <TableCell width={140} align="center" sx={cellSx.nowrap}>
                Status
              </TableCell>
            </TableRow>
          }
          renderRow={(row, index) => {
            const productId = Number(row.productId);
            const canNavigate = Number.isFinite(productId) && productId > 0;

            return (
              <>
                {/* Index-only navigation (consistent list UX). */}
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!canNavigate}
                    onClick={() => handleOpenDetail(productId)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {(page - 1) * pageSize + index + 1}
                  </Button>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{row.productCode || '-'}</TableCell>

                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {row.productName || '-'}
                  </Typography>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip label={row.stock?.stockCode ?? '-'} size="small" variant="outlined" />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip label={getSaleTypeLabel(row.productSaleType)} size="small" variant={getSaleTypeChipVariant(row.productSaleType)} />
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {toUsdDisplay(Number(row.price ?? 0))}
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {Number(row.lastWeekShippedCount ?? 0).toLocaleString()}
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {Number(row.totalShippedCount ?? 0).toLocaleString()}
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {Number(row.refundedCount ?? 0).toLocaleString()}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={getProductStatusLabel(row.productStatus)}
                    color={getProductStatusChipColor(row.productStatus)}
                    variant="filled"
                    size="small"
                    sx={{ fontWeight: 800 }}
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onChangePage={onChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default ProductsTable;
